import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  BookOpen, Clock, Users, Play, ChevronRight, ArrowLeft,
  SlidersHorizontal, ChevronDown, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function formatDuration(seconds) {
  if (!seconds) return '0 min';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (hrs > 0) return `${hrs}h ${mins > 0 ? `${mins}m` : ''}`;
  return `${mins} min`;
}

const CoursesPage = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [level, setLevel] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hoveredCourse, setHoveredCourse] = useState(null);

  useEffect(() => { fetchData(); }, [sortBy, selectedCategory, level]);

  const fetchData = async () => {
    try {
      let url = `${API_URL}/api/courses/public?sort_by=${sortBy}`;
      if (selectedCategory !== 'all') url += `&category_id=${selectedCategory}`;
      if (level) url += `&level=${level}`;
      const [coursesRes, catsRes] = await Promise.all([
        fetch(url),
        fetch(`${API_URL}/api/courses/categories`)
      ]);
      const coursesData = await coursesRes.json();
      const catsData = await catsRes.json();
      setCourses(coursesData.courses || []);
      setCategories(catsData.categories || []);
    } catch (err) {
      console.error('Failed to fetch courses:', err);
    } finally {
      setLoading(false);
    }
  };

  const getThumbnailUrl = (path) => path ? `${API_URL}/api/courses/files/${path}` : null;
  const getTrailerUrl = (courseId) => `${API_URL}/api/courses/trailer/${courseId}`;

  const SORT_OPTIONS = [
    { value: 'popular', label: 'Most Popular' },
    { value: 'newest', label: 'Newly Added' },
    { value: 'price_low', label: 'Price - Low to High' },
    { value: 'price_high', label: 'Price - High to Low' },
  ];

  const LEVELS = ['Beginner', 'Intermediate', 'Advanced'];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-black/90 backdrop-blur-sm z-50 border-b border-golden/30">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-golden hover:text-golden-light transition-colors" data-testid="back-home-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <a href="/" className="flex items-center">
              <img src="https://customer-assets.emergentagent.com/job_invest-links/artifacts/kydgz8tm_WhatsApp%20Image%202025-08-23%20at%203.47.06%20PM.jpeg" alt="Logo" className="h-10 w-auto" />
            </a>
          </div>
          <nav className="hidden md:flex items-center space-x-6">
            <button onClick={() => navigate('/')} className="text-golden hover:text-golden-light transition-colors">Home</button>
            <span className="text-golden font-semibold border-b-2 border-golden pb-1">Courses</span>
            <a href="/free-learning" className="text-golden hover:text-golden-light transition-colors">Free Learning</a>
            <a href="/login" className="bg-golden hover:bg-golden/90 text-black font-semibold px-4 py-2 rounded-lg transition-colors">Login</a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-24 pb-8 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-golden/10 via-transparent to-golden/5"></div>
        <div className="container mx-auto relative z-10">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2" data-testid="courses-heading">
            All <span className="text-golden">Courses</span>
          </h1>
        </div>
      </section>

      {/* Filters Bar */}
      <section className="px-4 pb-6 sticky top-[60px] z-40 bg-black/95 backdrop-blur-sm border-b border-gray-800">
        <div className="container mx-auto">
          <div className="flex items-center gap-3 overflow-x-auto py-2 scrollbar-hide">
            {/* Filters Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition-all flex-shrink-0 ${
                showFilters ? 'bg-golden text-black border-golden' : 'bg-white/5 text-gray-300 border-gray-700 hover:border-golden/50'
              }`}
              data-testid="filters-toggle-btn"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              <ChevronDown className={`w-3 h-3 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>

            {/* Category Pills */}
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex-shrink-0 ${
                selectedCategory === 'all' ? 'bg-golden text-black' : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-gray-700'
              }`}
            >
              All
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex-shrink-0 whitespace-nowrap ${
                  selectedCategory === cat.id ? 'bg-golden text-black' : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-gray-700'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Filter Dropdown */}
          {showFilters && (
            <div className="mt-3 bg-gray-900 border border-gray-700 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="filters-panel">
              {/* Sort By */}
              <div>
                <h4 className="text-sm font-semibold text-white mb-3">Sort By</h4>
                <div className="space-y-2">
                  {SORT_OPTIONS.map(opt => (
                    <label key={opt.value} className="flex items-center gap-2.5 cursor-pointer group">
                      <div className={`w-4.5 h-4.5 rounded-full border-2 flex items-center justify-center transition-colors ${
                        sortBy === opt.value ? 'border-golden bg-golden' : 'border-gray-500 group-hover:border-gray-400'
                      }`}>
                        {sortBy === opt.value && <div className="w-1.5 h-1.5 rounded-full bg-black" />}
                      </div>
                      <span className={`text-sm ${sortBy === opt.value ? 'text-white font-medium' : 'text-gray-400'}`}>{opt.label}</span>
                      <input type="radio" name="sort" value={opt.value} checked={sortBy === opt.value} onChange={() => setSortBy(opt.value)} className="hidden" />
                    </label>
                  ))}
                </div>
              </div>

              {/* Level */}
              <div>
                <h4 className="text-sm font-semibold text-white mb-3">Level</h4>
                <div className="space-y-2">
                  <label className="flex items-center gap-2.5 cursor-pointer group">
                    <div className={`w-4.5 h-4.5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      !level ? 'border-golden bg-golden' : 'border-gray-500 group-hover:border-gray-400'
                    }`}>
                      {!level && <div className="w-1.5 h-1.5 rounded-full bg-black" />}
                    </div>
                    <span className={`text-sm ${!level ? 'text-white font-medium' : 'text-gray-400'}`}>All Levels</span>
                    <input type="radio" name="level" checked={!level} onChange={() => setLevel('')} className="hidden" />
                  </label>
                  {LEVELS.map(l => (
                    <label key={l} className="flex items-center gap-2.5 cursor-pointer group">
                      <div className={`w-4.5 h-4.5 rounded-full border-2 flex items-center justify-center transition-colors ${
                        level === l ? 'border-golden bg-golden' : 'border-gray-500 group-hover:border-gray-400'
                      }`}>
                        {level === l && <div className="w-1.5 h-1.5 rounded-full bg-black" />}
                      </div>
                      <span className={`text-sm ${level === l ? 'text-white font-medium' : 'text-gray-400'}`}>{l}</span>
                      <input type="radio" name="level" value={l} checked={level === l} onChange={() => setLevel(l)} className="hidden" />
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Course Grid */}
      <section className="px-4 pb-20">
        <div className="container mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white/5 rounded-xl h-80 animate-pulse"></div>
              ))}
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl text-gray-400">No courses available yet</h3>
              <p className="text-gray-500 mt-2">Check back soon for new courses!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="courses-grid">
              {courses.map(course => (
                <CourseCard
                  key={course.id}
                  course={course}
                  getThumbnailUrl={getThumbnailUrl}
                  getTrailerUrl={getTrailerUrl}
                  hoveredCourse={hoveredCourse}
                  setHoveredCourse={setHoveredCourse}
                  onClick={() => navigate(`/courses/${course.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-golden/20 py-8 px-4 bg-black/50">
        <div className="container mx-auto text-center">
          <p className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} Upper Circuit Stocks. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

const CourseCard = ({ course, getThumbnailUrl, getTrailerUrl, hoveredCourse, setHoveredCourse, onClick }) => {
  const videoRef = useRef(null);
  const isHovered = hoveredCourse === course.id;

  useEffect(() => {
    if (isHovered && course.has_trailer && videoRef.current) {
      videoRef.current.play().catch(() => {});
    } else if (!isHovered && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isHovered, course.has_trailer]);

  return (
    <Card
      className="bg-white/5 border-golden/20 hover:border-golden/50 transition-all cursor-pointer group overflow-hidden"
      onClick={onClick}
      onMouseEnter={() => setHoveredCourse(course.id)}
      onMouseLeave={() => setHoveredCourse(null)}
      data-testid={`course-card-${course.id}`}
    >
      <div className="relative h-48 bg-gradient-to-br from-golden/20 to-golden/5 overflow-hidden">
        {/* Thumbnail (default) */}
        {course.thumbnail_path ? (
          <img
            src={getThumbnailUrl(course.thumbnail_path)}
            alt={course.title}
            className={`w-full h-full object-cover transition-opacity duration-300 ${isHovered && course.has_trailer ? 'opacity-0' : 'opacity-100'}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Play className="w-16 h-16 text-golden/40" />
          </div>
        )}
        {/* Trailer video (on hover) */}
        {course.has_trailer && (
          <video
            ref={videoRef}
            src={isHovered ? getTrailerUrl(course.id) : undefined}
            muted
            loop
            playsInline
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
          />
        )}
        {course.category_name && (
          <Badge className="absolute top-3 left-3 bg-golden/90 text-black text-xs z-10">
            {course.category_name}
          </Badge>
        )}
        {course.level && (
          <Badge className="absolute top-3 right-3 bg-black/70 text-white text-[10px] z-10 border border-white/20">
            {course.level}
          </Badge>
        )}
        {course.has_trailer && !isHovered && (
          <div className="absolute bottom-2 left-2 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded flex items-center gap-1 z-10">
            <Play className="w-3 h-3" /> Preview
          </div>
        )}
      </div>
      <CardContent className="p-4">
        <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2 group-hover:text-golden transition-colors">
          {course.title}
        </h3>
        <p className="text-gray-400 text-sm mb-3 line-clamp-2">{course.description}</p>
        <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDuration(course.total_duration_seconds)}</span>
          <span className="flex items-center gap-1"><Play className="w-3 h-3" />{course.lesson_count} lessons</span>
          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{course.enrolled_count || 0}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="text-golden text-xl font-bold">
              {course.price > 0 ? `₹${course.price.toLocaleString()}` : 'Free'}
            </span>
            {course.original_price > course.price && (
              <span className="text-gray-500 text-sm line-through">₹{course.original_price.toLocaleString()}</span>
            )}
          </div>
          <ChevronRight className="w-5 h-5 text-golden group-hover:translate-x-1 transition-transform" />
        </div>
      </CardContent>
    </Card>
  );
};

export default CoursesPage;
